import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { useEffect } from "react";
import { initializeMobileApp, getSafeAreaStyle } from "@/lib/mobileInit";
import { usePlatform } from "@/hooks/usePlatform";
import { useNavigate } from "react-router-dom";

import Index from "./pages/Index";
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";
import SuperAdmin from "./pages/SuperAdmin";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import HostLogin from "./pages/HostLogin";
import VenueAdminLogin from "./pages/VenueAdminLogin";
import ResetPassword from "./pages/ResetPassword";
import HostDashboard from "./pages/HostDashboard";
import HostShowMode from "./pages/HostShowMode";
import HostSettingsPage from "./pages/HostSettings";
import HostManageMode from "./pages/HostManageMode";
import SingerPage from "./pages/SingerPage";
import VenueSelection from "./pages/VenueSelection";
import QueueProjection from "./pages/QueueProjection";
import SubscriptionPage from "./pages/SubscriptionPage";
import GlobalPricingPage from "./pages/GlobalPricingPage";
import BillingCallback from "./pages/BillingCallback";

const queryClient = new QueryClient();

const App = () => {
  const platform = usePlatform();
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize mobile features when app loads
    initializeMobileApp();

    // If running as a native mobile app, skip the landing index and go straight to auth
    if (platform.isNative) {
      try {
        // Replace history so landing isn't reachable via back
        navigate?.("/auth", { replace: true });
      } catch (e) {
        // ignore navigation errors in environments without router
      }
    }
  }, [platform.isNative, navigate]);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
          <div style={platform.isNative ? getSafeAreaStyle() : undefined}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/cook" element={<SuperAdminLogin />} />
            <Route path="/super-admin" element={<SuperAdmin />} />
            {/* Venue admin login — canonical URL */}
            <Route path="/venue" element={<VenueAdminLogin />} />
            {/* Backward-compat redirects for existing venue admin bookmarks */}
            <Route path="/host" element={<Navigate to="/venue" replace />} />
            <Route path="/host-login" element={<Navigate to="/venue" replace />} />
            {/* Venue admin password reset (by username, emailed OTP) */}
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Show host login */}
            <Route path="/show-host" element={<HostLogin />} />
            <Route path="/host-dashboard" element={<HostDashboard />} />
            <Route path="/host-show-mode" element={<HostShowMode />} />
            {/* Host-only settings page (announcements + queue rules) */}
            <Route path="/host-settings" element={<HostSettingsPage />} />
            {/* <Route path="/host-manage-mode" element={<HostManageMode />} /> */}
            <Route path="/venues" element={<VenueSelection />} />
            <Route path="/singer/:venue_slug" element={<SingerPage />} />
            <Route path="/demo-singer" element={<SingerPage demo />} />
            <Route path="/demo" element={<HostDashboard demo />} />
            <Route path="/demo-host/show" element={<HostShowMode demo />} />
            <Route path="/queue/:venue_slug" element={<QueueProjection />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/international" element={<GlobalPricingPage />} />
            <Route path="/billing/callback" element={<BillingCallback />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </div>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
